import { useEffect, useState } from "react";

import AppModal from "@/components/ui/AppModal";
import MultiSelect, {
  type MultiSelectOption,
} from "@/components/ui/MultiSelect";
import {
  DEFAULT_COUPON_FORM,
  DISCOUNT_TYPE_OPTIONS,
  type CouponFormValues,
} from "@features/coupons/couponUtils";

interface CouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  mode: "create" | "edit";
  initialValues?: CouponFormValues | null;
  contentOptions: MultiSelectOption[];
  onSubmit: (values: CouponFormValues) => void;
  submitting?: boolean;
}

export default function CouponModal({
  isOpen,
  onClose,
  title,
  mode,
  initialValues,
  contentOptions,
  onSubmit,
  submitting = false,
}: CouponModalProps) {
  const [form, setForm] = useState<CouponFormValues>(DEFAULT_COUPON_FORM);

  // Reset/prefill whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setForm(
      mode === "edit" && initialValues
        ? initialValues
        : { ...DEFAULT_COUPON_FORM },
    );
  }, [isOpen, mode, initialValues]);

  const resolvedTitle =
    title ?? (mode === "edit" ? "Edit Coupon" : "Create Coupon");

  // MultiSelect is generic (multi-select), but this field only allows one
  // item for now — keep just the most recently toggled value.
  const handleContentChange = (vals: string[]) => {
    setForm((f) => ({ ...f, contentIds: vals.slice(-1) }));
  };

  const handleEmployeeCodeChange = (checked: boolean) => {
    setForm((f) => ({
      ...f,
      employeeCode: checked,
      // mutually exclusive with the two single-use flags
      oneUsePerCompany: checked ? false : f.oneUsePerCompany,
      oneUsePerUser: checked ? false : f.oneUsePerUser,
    }));
  };

  const handleSingleUseChange = (
    field: "oneUsePerCompany" | "oneUsePerUser",
    checked: boolean,
  ) => {
    setForm((f) => ({
      ...f,
      [field]: checked,
      // mutually exclusive with employee code
      employeeCode: checked ? false : f.employeeCode,
    }));
  };

  const singleUseDisabled = form.employeeCode;
  const employeeCodeDisabled = form.oneUsePerCompany || form.oneUsePerUser;

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={resolvedTitle}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: "var(--sidebar-bg)" }}
          >
            {mode === "edit" ? "Save Changes" : "Create Coupon"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Internal Code Name">
            <input
              type="text"
              value={form.internalCodeName}
              onChange={(e) =>
                setForm((f) => ({ ...f, internalCodeName: e.target.value }))
              }
              className={inputClass}
              placeholder="e.g. summer-promo"
            />
          </Field>

          <Field label="Promo Code">
            <input
              type="text"
              value={form.promoCode}
              onChange={(e) =>
                setForm((f) => ({ ...f, promoCode: e.target.value }))
              }
              className={inputClass}
              placeholder="e.g. PROMO123"
            />
          </Field>
        </div>

        <Field label="Content">
          <MultiSelect
            value={form.contentIds}
            onChange={handleContentChange}
            options={contentOptions}
            placeholder="Select content…"
            disabled={contentOptions.length === 0 || initialValues != null}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Discount Type">
            <select
              value={form.discountType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  discountType: e.target
                    .value as CouponFormValues["discountType"],
                }))
              }
              className={inputClass}
            >
              {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Value">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.value}
              onChange={(e) =>
                setForm((f) => ({ ...f, value: e.target.value }))
              }
              className={inputClass}
              placeholder="e.g. 5.3"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Start Date">
            <input
              type="date"
              value={form.startDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, startDate: e.target.value }))
              }
              className={inputClass}
            />
          </Field>

          <Field label="End Date (optional)">
            <input
              type="date"
              value={form.endDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, endDate: e.target.value }))
              }
              className={inputClass}
            />
          </Field>
        </div>

        <div className="space-y-2 rounded-xl border border-neutral-100 p-3 dark:border-neutral-800">
          <Checkbox
            label="Single Use (by Company)"
            checked={form.oneUsePerCompany}
            disabled={singleUseDisabled}
            onChange={(checked) =>
              handleSingleUseChange("oneUsePerCompany", checked)
            }
          />
          <Checkbox
            label="Single Use (by Attendee)"
            checked={form.oneUsePerUser}
            disabled={singleUseDisabled}
            onChange={(checked) =>
              handleSingleUseChange("oneUsePerUser", checked)
            }
          />
          <Checkbox
            label="Employee Code"
            checked={form.employeeCode}
            disabled={employeeCodeDisabled}
            onChange={handleEmployeeCodeChange}
          />
        </div>
      </div>
    </AppModal>
  );
}

const inputClass =
  "h-9 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-800 outline-none transition-colors focus:border-[var(--sidebar-bg)] focus:ring-2 focus:ring-[var(--sidebar-bg)]/20 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function Checkbox({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200 ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 cursor-pointer rounded accent-[var(--sidebar-bg)] disabled:cursor-not-allowed"
      />
      {label}
    </label>
  );
}
